import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './AssetManager.css';
import { uploadAssetToS3, validateAssetFile, createAssetFromS3Upload } from '../../services/S3AssetUploader';
import { apiFetch } from '../../utils/apiFetch';
import { normalizeUploadedAsset } from '../../utils/uploadAssetUrlResolver';
import ImageWithFallback from '../ImageWithFallback';
import {
  PREMIUM_PLAN_NAME,
  getTierConfig,
  isPremiumTier
} from '../../config/entitlements';
import { getAssetUsageSnapshot } from '../../services/EntitlementService';
import { resolveRuntimeTier } from '../../utils/runtimeTier';

/**
 * AssetManager Component - S3-Based Asset Upload System
 * @date_of_change: 2025-10-01 - Migrated from base64 to S3 architecture
 *
 * This component provides a single, streamlined interface for uploading both
 * maps and tokens to the BattleRoom using S3 cloud storage. It automatically
 * places assets on the correct layer based on what layer is currently active.
 *
 * ARCHITECTURE UPGRADE:
 * - Replaces inefficient base64 data URLs with S3 CDN URLs
 * - Eliminates 7MB images being sent through WebSocket messages
 * - Uses backend S3 upload API for secure, scalable asset management
 * - Assets are stored permanently and accessible via CDN URLs
 *
 * WHAT IT DOES:
 * - Provides "Add Assets" button for uploading image files to S3
 * - Supports drag-and-drop file upload (drag images directly onto the button)
 * - Automatically determines asset type based on active layer
 * - Shows upload progress and provides user feedback
 * - Handles multiple file uploads at once with validation
 * - Returns lightweight S3 URLs instead of heavy base64 data
 *
 * SMART LAYER ASSIGNMENT:
 * - Map Layer active → uploads become background maps
 * - Token Layer active → uploads become character/monster tokens
 * - GameMaster Layer active → uploads become GM-only assets
 * - Assets are automatically assigned the correct type and properties
 *
 * SUPPORTED FILE TYPES:
 * - PNG images (best for tokens with transparency) - up to 10MB
 * - JPEG images (good for maps and backgrounds) - up to 50MB for maps
 * - GIF images (supported but not recommended for large files)
 * - WebP images (modern format with good compression)
 *
 * USER EXPERIENCE:
 * - Click "Add Assets" to open file picker
 * - Or drag image files directly onto the button
 * - Visual feedback during S3 upload (loading state, drag-over highlighting)
 * - Error handling for upload failures and unsupported file types
 * - File size validation and meaningful error messages
 * - Compact design that fits in the toolbar without taking too much space
 *
 * INTEGRATION:
 * - Receives activeLayer from LayerControls to know where to place assets
 * - Calls onAssetUpload with S3 asset data and layer information
 * - Works with useBattleRoom's upload handlers for maps and tokens
 * - Uses S3AssetUploader service for backend integration
 * - Styled to match the overall Roll20-style dark theme
 */

const AssetManager = ({
  activeLayer,
  onAssetUpload,
  uiMode,
  forcedLayer,
  uploadLabel = 'Upload',
  browseLabel,
  showIcons = true
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // @date_of_change: 2025-10-04 - Added browse existing images feature
  const [showBrowser, setShowBrowser] = useState(false);
  const [userAssets, setUserAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [browserError, setBrowserError] = useState(null);
  // Auth-aware thumbnails for protected endpoints
  const [thumbUrls, setThumbUrls] = useState({}); // { [assetId]: objectUrl }
  const [assetUsage, setAssetUsage] = useState({ totalCount: 0, totalBytes: 0 });
  const tier = resolveRuntimeTier();
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);
  const assetCap = tierConfig.limits.assetCount;
  const atAssetCap = !isPremium && assetCap !== 'unlimited' && assetUsage.totalCount >= assetCap;

  const refreshAssetUsage = React.useCallback(async () => {
    const usage = await getAssetUsageSnapshot();
    setAssetUsage(usage);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vcs-asset-usage-updated', {
        detail: usage
      }));
    }
  }, []);

  /**
   * Handle file selection and upload to S3
   * @date_of_change: 2025-10-01 - Replaced base64 FileReader with S3 uploads
   * This eliminates 7MB images being sent through WebSocket messages
   */
  const handleFileSelect = async (files) => {
    if (atAssetCap) {
      setUploadError(`Your free asset vault is full at ${assetCap} uploads. Clear a slot or upgrade to ${PREMIUM_PLAN_NAME}.`);
      return;
    }

    if (!files || files.length === 0) {
      console.warn('[AssetManager] No files provided or empty file list');
      return;
    }

    console.log('[AssetManager] File selection started:', files.length, 'files for S3 upload');
    setIsUploading(true);
    setUploadError(null);

    // Convert FileList to Array more safely
    const fileArray = files instanceof FileList ? Array.from(files) : files;
    console.log('[AssetManager] Processing files for S3 upload:', fileArray.map(f => f.name));

    try {
      // Process files sequentially to avoid overwhelming S3/backend
      for (const file of fileArray) {
        console.log('[AssetManager] Processing file for S3:', file.name, file.type, file.size, 'bytes');

        if (!file || !file.type || !file.type.startsWith('image/')) {
          console.warn('[AssetManager] Skipping invalid or non-image file:', file);
          continue;
        }

        try {
          const assetType = getAssetType();
          const targetLayer = getTargetLayer();

          // Validate file before upload
          validateAssetFile(file, assetType);
          console.log('[AssetManager] Uploading to S3 with type:', assetType, 'for layer:', targetLayer);

          // Upload to S3 and get CDN URL
          const uploadResult = await uploadAssetToS3(file, assetType);
          console.log('[AssetManager] S3 upload successful:', uploadResult.url);

          // Create asset object with S3 URL instead of base64
          const asset = createAssetFromS3Upload(uploadResult, file, targetLayer);

          console.log('[AssetManager] Asset created with S3 URL:', asset.type, 'for layer:', targetLayer);
          console.log('[AssetManager] Asset URL (lightweight):', asset.url);

          if (onAssetUpload) {
            console.log('[AssetManager] Calling onAssetUpload handler with S3 asset');
            onAssetUpload(asset);
          } else {
            console.error('[AssetManager] No onAssetUpload handler provided!');
          }

          await refreshAssetUsage();
          if (showBrowser) {
            await fetchUserAssets();
          }

        } catch (fileError) {
          console.error('[AssetManager] Error processing file:', file.name, fileError);
          // @date_of_change: 2025-10-04 - Improved error messages with actionable guidance
          const errorMsg = fileError.message.toLowerCase();
          let userMessage = `Failed to upload ${file.name}: ${fileError.message}`;

          if (errorMsg.includes('size') || errorMsg.includes('large')) {
            userMessage += '. Try reducing the image size or use a different file format.';
          } else if (errorMsg.includes('type') || errorMsg.includes('format')) {
            userMessage += '. Only PNG, JPEG, GIF, and WebP images are supported.';
          } else if (errorMsg.includes('cors') || errorMsg.includes('vault did not accept the upload') || errorMsg.includes('bucket')) {
            userMessage += '. The image vault still needs its storage gate opened for this site. If this keeps happening, contact info@sixsmithgames.com.';
          } else if (errorMsg.includes('network') || errorMsg.includes('timeout')) {
            userMessage += '. Check your internet connection and try again.';
          } else {
            userMessage += '. Try a different image or contact support if the issue persists.';
          }

          setUploadError(userMessage);
          // Continue with other files
        }
      }

    } catch (error) {
      console.error('[AssetManager] Batch upload error:', error);
      const errorMsg = error.message || 'Unknown error occurred';
      setUploadError(`Upload failed: ${errorMsg}. Please try again or use a different image.`);
    } finally {
      setIsUploading(false);
    }
  };

  const getAssetType = React.useCallback(() => {
    // Force 'map' mode when explicitly requested by UI
    if (uiMode === 'map') return 'map';
    if (uiMode === 'token') return 'token';
    // Otherwise, use the active layer to determine the asset type
    switch(activeLayer) {
      case 'map':
        return 'map';
      case 'token':
      case 'gamemaster':
      default:
        return 'token';
    }
  }, [activeLayer, uiMode]);

  const getTargetLayer = React.useCallback(() => {
    if (forcedLayer) {
      return forcedLayer;
    }
    if (uiMode === 'map') {
      return 'map';
    }
    if (uiMode === 'token') {
      return 'token';
    }
    return activeLayer || 'token';
  }, [activeLayer, forcedLayer, uiMode]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const suppressToolbarEvent = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
  };

  const handleClick = () => {
    if (atAssetCap) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      console.log('[AssetManager] File input change event:', e.target.files);
      if (e.target.files && e.target.files.length > 0) {
        handleFileSelect(e.target.files);
      } else {
        console.warn('[AssetManager] No files selected or files is null');
      }
    };
    input.click();
  };

  const handleUploadPointerDown = (event) => {
    suppressToolbarEvent(event);
    handleClick();
  };

  const handleBrowsePointerDown = (event) => {
    suppressToolbarEvent(event);
    setShowBrowser(true);
  };

  // getLayerIcon removed (unused)

  const getLayerName = () => {
    switch (getTargetLayer()) {
      case 'map': return 'Map Layer';
      case 'token': return 'Token Layer';
      case 'gamemaster': return 'GM Layer';
      default: return 'Unknown Layer';
    }
  };

  /**
   * Fetch user's uploaded assets from backend
   * @date_of_change: 2025-10-04 - Added asset browsing feature
   */
  const fetchUserAssets = React.useCallback(async () => {
    setLoadingAssets(true);
    setBrowserError(null);

    const assetType = getAssetType();
    const assetLabelPlural = assetType === 'map' ? 'maps' : 'tokens';
    const listUrl = `/api/s3-uploads/list?type=${assetType}`;
    console.log('[AssetManager] fetchUserAssets', {
      timestamp: new Date().toISOString(),
      assetType,
      listUrl
    });

    try {
      let response = await apiFetch(listUrl, { requireAuth: true });
      if (response.status === 401) {
        console.warn('[AssetManager] 401 on first attempt, retrying after Clerk warmup');
        await new Promise(r => setTimeout(r, 600));
        response = await apiFetch(listUrl, { requireAuth: true });
      }

      if (!response.ok) {
        let reason = `HTTP ${response.status}`;
        try {
          const payload = await response.clone().json();
          reason = payload?.message || payload?.error || reason;
        } catch (_parseError) {
          // Keep the fallback HTTP status message.
        }
        setBrowserError(
          `Could not open your ${assetLabelPlural} vault right now: ${reason}. Refresh and try again. If it keeps happening, contact info@sixsmithgames.com.`
        );
        setUserAssets([]);
        return;
      }

      const data = await response.json();
      let assets = Array.isArray(data) ? data : (data?.assets || data?.data?.assets || data?.data || []);
      if (!Array.isArray(assets)) assets = [];

      if (assets.length === 0) {
        setUserAssets([]);
        setBrowserError(null);
        return;
      }

      // Ensure each asset has both a direct URL and a proxy URL
      const processed = assets.map((asset) => normalizeUploadedAsset(asset));

      console.log('[AssetManager] Processed assets:', processed.length);
      setUserAssets(processed);
      setBrowserError(null);
    } catch (error) {
      console.error('[AssetManager] Failed to fetch user assets:', error);
      const msg = error?.message || 'Unknown error';
      setBrowserError(
        `Error loading your ${assetLabelPlural}: ${msg}. Refresh and try again. If it keeps happening, contact info@sixsmithgames.com.`
      );
      setUserAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  }, [getAssetType]);

  /**
   * Load assets when browser is opened
   */
  useEffect(() => {
    if (showBrowser) {
      fetchUserAssets();
    }
  }, [showBrowser, fetchUserAssets]);

  useEffect(() => {
    refreshAssetUsage();
  }, [refreshAssetUsage]);

  // Build auth-aware thumbnails for assets that require Authorization (e.g., backend proxy)
  useEffect(() => {
    let cancelled = false;
    // Cleanup existing object URLs when list changes
    const previousUrls = new Set(Object.values(thumbUrls));

    async function buildThumbs() {
      const entries = await Promise.all(userAssets.map(async (asset) => {
        // Prefer the normalized app URL so uploads always resolve through first-party paths
        if (asset.url) {
          return [asset.id, asset.url];
        }
        // If we only have a proxy URL, fetch it with Authorization and create a blob URL
        if (asset.proxyUrl) {
          try {
            const resp = await apiFetch(asset.proxyUrl, { method: 'GET' });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const objUrl = URL.createObjectURL(blob);
            return [asset.id, objUrl];
          } catch (e) {
            // Fall back to proxy URL; ImageWithFallback will handle errors
            return [asset.id, asset.proxyUrl];
          }
        }
        return [asset.id, asset.fallbackUrl || asset.url];
      }));

      if (cancelled) return;
      // Revoke any previous object URLs
      previousUrls.forEach(u => { try { if (u && u.startsWith('blob:')) URL.revokeObjectURL(u); } catch (_) {} });

      const next = {};
      for (const [id, src] of entries) next[id] = src;
      setThumbUrls(next);
    }

    if (userAssets && userAssets.length) {
      buildThumbs();
    } else {
      // Clear thumbs when no assets
      Object.values(thumbUrls).forEach(u => { try { if (u && u.startsWith('blob:')) URL.revokeObjectURL(u); } catch (_) {} });
      setThumbUrls({});
    }

    return () => {
      cancelled = true;
      // Revoke on unmount
      Object.values(thumbUrls).forEach(u => { try { if (u && u.startsWith('blob:')) URL.revokeObjectURL(u); } catch (_) {} });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAssets]);

  /**
   * Handle deleting an asset
   * @date_of_change: 2025-10-04 - Added asset deletion feature
   */
  const handleAssetDelete = async (assetId, event) => {
    // Prevent event bubbling to parent onClick (which would select the asset)
    event.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete this ${getAssetType() === 'map' ? 'map' : 'token'}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiFetch(`/api/s3-uploads/${assetId}`, {
        method: 'DELETE',
        requireAuth: true
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || payload?.error || 'Failed to delete asset');
      }

      // Refresh the asset list
      await fetchUserAssets();
      await refreshAssetUsage();
    } catch (error) {
      console.error('[AssetManager] Failed to delete asset:', error);
      setBrowserError(error?.message || `Failed to delete ${getAssetType() === 'map' ? 'map' : 'token'}. Please try again or contact support.`);
    }
  };

  /**
   * Handle selecting an existing asset from the browser
   */
  const handleAssetSelect = async (asset) => {
    console.log('[AssetManager] User selected existing asset:', asset.url);

    try {
      const assetType = getAssetType();

      // Create asset object matching the format expected by onAssetUpload
      // Extract filename from key (e.g., "uploads/userId/token/123-abc.png" -> "123-abc.png")
      const filename = asset.key.split('/').pop() || 'uploaded-image.png';
      // Prefer the normalized first-party app URL so saved battle-room state never depends on raw S3 access.
      const selectedUrl = asset.url || asset.proxyUrl || asset.fallbackUrl || asset.directUrl;

      const assetData = {
        id: asset.id,
        name: filename,
        type: assetType,
        layer: getTargetLayer(),
        url: selectedUrl,
        s3Key: asset.key,
        width: assetType === 'map' ? 800 : 100,
        height: assetType === 'map' ? 600 : 100,
        x: 100,
        y: 100,
        uploadedAt: asset.uploadedAt,
        fileSize: asset.size
      };

      // Call the upload handler with the selected asset
      // Same signature as regular upload: single asset object
      if (onAssetUpload) {
        onAssetUpload(assetData);
      }

      // Close the browser after a brief delay to show feedback
      setTimeout(() => {
        setShowBrowser(false);
      }, 100);
    } catch (error) {
      console.error('[AssetManager] Failed to use selected asset:', error);
      // @date_of_change: 2025-10-04 - Improved error message with actionable guidance
      const errorMsg = error.message || 'Unknown error';
      setBrowserError(`Could not add this ${nounSingular}: ${errorMsg}. Try uploading a fresh one instead.`);
    }
  };

  const isMapMode = (uiMode === 'map') || getAssetType() === 'map';
  const nounSingular = isMapMode ? 'map' : 'token';
  const nounPlural = isMapMode ? 'maps' : 'tokens';
  const resolvedBrowseLabel = browseLabel || (isMapMode ? 'Maps' : 'Tokens');
  const browserModal = showBrowser && typeof document !== 'undefined'
    ? createPortal(
        <div
          className="user-asset-browser-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            zIndex: 10000
          }}
          onClick={() => setShowBrowser(false)}
        >
          <div
            className="user-asset-browser-content"
            style={{
              background: '#36393f',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              width: 'min(90vw, 800px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0, color: '#fff' }}>Your Uploaded {isMapMode ? 'Maps' : 'Tokens'}</h3>
              <button
                onClick={() => setShowBrowser(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#b9bbbe',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>

            {loadingAssets ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#b9bbbe' }}>
                <div className="spinner"></div>
                <p>Loading your {nounPlural}...</p>
              </div>
            ) : browserError ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#ff6b6b' }}>
                {browserError}
              </div>
            ) : userAssets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#b9bbbe' }}>
                <p>No uploaded {nounPlural} found.</p>
                <p style={{ fontSize: '12px', marginTop: '8px' }}>Upload some {nounPlural} first to see them here.</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '12px'
              }}>
                {userAssets.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => handleAssetSelect(asset)}
                    style={{
                      cursor: 'pointer',
                      border: '2px solid #4a4d52',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      transition: 'border-color 0.2s',
                      background: '#2c2f33',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#7289da'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4d52'}
                  >
                    <ImageWithFallback
                      src={thumbUrls[asset.id] || asset.url || asset.proxyUrl || asset.directUrl}
                      alt="User uploaded asset"
                      fallback={asset.type === 'map' ? 'map' : 'token-default'}
                      style={{
                        width: '100%',
                        height: '150px',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    <button
                      onClick={(e) => handleAssetDelete(asset.id, e)}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'rgba(220, 0, 0, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 0, 0, 0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(220, 0, 0, 0.8)'}
                      title={`Delete this ${nounSingular}`}
                    >
                      ×
                    </button>
                    <div style={{
                      padding: '8px',
                      fontSize: '10px',
                      color: '#b9bbbe',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {new Date(asset.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`asset-manager compact ${showIcons ? '' : 'iconless'}`.trim()}>
      <div
        className={`upload-zone compact ${dragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''} ${atAssetCap ? 'locked' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onMouseDown={handleUploadPointerDown}
        onClick={suppressToolbarEvent}
        title={atAssetCap
          ? `Free asset vault full. Upgrade to ${PREMIUM_PLAN_NAME}.`
          : (isMapMode ? 'Upload a new battle map' : `Upload a new token to ${getLayerName()}`)}
      >
        {isUploading ? (
          <div className="upload-status compact">
            <div className="spinner small"></div>
            <span style={{fontSize: '10px', marginTop: '4px'}}>Uploading to S3...</span>
          </div>
        ) : (
          <div className="upload-content compact">
            {showIcons && (
              <div className="upload-icon small">{isMapMode ? '🗺️' : '📁'}</div>
            )}
            <span className="upload-text compact">{uploadLabel}</span>
          </div>
        )}
      </div>

      {/* Browse Existing Images Button */}
      <button
        type="button"
        className="browse-button"
        onMouseDown={handleBrowsePointerDown}
        onClick={suppressToolbarEvent}
        title={`Browse your uploaded ${nounPlural}`}
      >
        {showIcons ? (
          isMapMode ? `🗺️ ${resolvedBrowseLabel}` : `📂 ${resolvedBrowseLabel}`
        ) : (
          resolvedBrowseLabel
        )}
      </button>

      {uploadError && (
        <div className="upload-error compact">
          {uploadError}
        </div>
      )}
      {browserModal}
    </div>
  );
};

export default AssetManager;
